import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import { useRemainingCredits } from "lib/hooks/api/admin/credits/useRemainingCredits";
import { useSubscription } from "lib/hooks/subscription";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import { routes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";

import BID_REPORT_DEMO_GIF from "../../../assets/images/bid_report_demo.gif";

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  newOpportunityId?: string;
};

export default function BidNoBidPromptModal({ isOpen, onOpenChange, newOpportunityId }: Props) {
  const { logEvent } = useTracking();
  const subscription = useSubscription();

  const hasBidsDataType = subscription.hasDataTypes("BIDS");
  const { data: credits } = useRemainingCredits(
    { creditType: "BID" },
    { enabled: !hasBidsDataType },
  );
  const hasBidsAccess = Boolean(hasBidsDataType || (credits && credits.remainingCredits > 0));

  const [showBidNoBidPromptModal, setShowBidNoBidPromptModal] = useLocalStorage<boolean>(
    "SHOW_BID_NO_BID_PROMPT_MODAL",
    true,
  );

  useEffect(() => {
    if (isOpen && hasBidsAccess && showBidNoBidPromptModal) {
      logEvent(EventNames.viewedBidNoBidPromptModal);
    }
  }, [isOpen, hasBidsAccess, showBidNoBidPromptModal, logEvent]);

  const handleDismissAndDontShowAgain = () => {
    logEvent(EventNames.clickedBidNoBidPromptModal, {
      "Option selected": "Dismiss & don't show again",
    });
    setShowBidNoBidPromptModal(false);
    onOpenChange(false);
  };

  const handleViewOpportunity = () => {
    logEvent(EventNames.clickedBidNoBidPromptModal, {
      "Option selected": "View opportunity",
    });
    window.location.href = `${routes.opportunities}/${newOpportunityId}`;
  };

  const handleGenerateReport = () => {
    logEvent(EventNames.clickedBidNoBidPromptModal, {
      "Option selected": "Generate bid / no-bid report",
    });
    window.location.href = `${routes.opportunities}/${newOpportunityId}/bid_qualification/`;
  };

  if (!hasBidsAccess || !showBidNoBidPromptModal) {
    return null;
  }

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          logEvent(EventNames.clickedBidNoBidPromptModal, {
            "Option selected": "Close",
          });
        }
        onOpenChange(open);
      }}
      maxWidth="1200px"
    >
      <Container>
        <BodyContainer>
          <Content>
            <Text h1>Make a quick bid / no-bid decision</Text>
            <Text>
              StotlesAI extracts critical facts from tender documents to speed up the decision
              process.
            </Text>
            <Text>Upload this tender’s documents to get started.</Text>
          </Content>
          <GifContainer>
            <Gif src={BID_REPORT_DEMO_GIF} alt="Bid report demo" />
          </GifContainer>
        </BodyContainer>
        <Flex gap={16} justifyContent="flex-end">
          <Button buttonType="tertiary" onClick={handleDismissAndDontShowAgain}>
            Dismiss & don’t show again
          </Button>
          <Button buttonType="secondary" onClick={handleViewOpportunity}>
            View opportunity
          </Button>
          <Button onClick={handleGenerateReport}>Generate bid / no-bid report</Button>
        </Flex>
      </Container>
    </Modal>
  );
}

const Container = styled(Flex)({
  flexDirection: "column",
  padding: 24,
  gap: 40,
});

const BodyContainer = styled(Flex)({
  flexDirection: "row",
  gap: 20,
  "@media (max-width: 960px)": {
    flexDirection: "column-reverse",
  },
});

const Content = styled(Flex)({
  width: 300,
  flexShrink: 0,
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  gap: 24,
  "@media (max-width: 960px)": {
    width: "100%",
  },
});

const GifContainer = styled.div({
  flex: 1,
  position: "relative",
  aspectRatio: "16 / 9",
});

const Gif = styled.img({
  borderRadius: 8,
  width: "100%",
  height: "100%",
  objectFit: "contain",
  cursor: "pointer",
  "&:focus-visible": {
    outline: "none",
  },
});
